import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, Navigate, useLocation } from 'react-router-dom';
import './Main.css';
import ConferenceLinks from './ConferenceLinks';

interface Conference {
  name: string;
  deadline: string;
  category: string;
  link: string;
  location: string;
  rating: string;
}

type SortKey = 'deadline' | 'category' | 'rating' | 'daysLeft';

type SortOrder = 'asc' | 'desc';

const Navigation = () => {
  const location = useLocation();
  return (
    <nav className="main-nav">
      <div className="nav-links">
        <Link to="/" className={location.pathname === '/' ? 'active' : ''}>
          Conference Links
        </Link>
        <Link to="/deadlines" className={location.pathname === '/deadlines' ? 'active' : ''}>
          Conference Deadlines
        </Link>
      </div>
      <a 
        href="https://github.com/Lin-Mao/conf-ddl" 
        target="_blank" 
        rel="noopener noreferrer"
        className="github-link"
        title="View source on GitHub"
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
        </svg>
      </a>
    </nav>
  );
};

function Main() {
  const [conferences, setConferences] = useState<Conference[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [siteTitle, setSiteTitle] = useState('Deadlines');
  const [sortKey, setSortKey] = useState<SortKey>('deadline');
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc');
  const [categoryFilter, setCategoryFilter] = useState(() => {
    const saved = localStorage.getItem('categoryFilter');
    return saved || '';
  });
  const [showPassed, setShowPassed] = useState(() => {
    const saved = localStorage.getItem('showPassed');
    return saved === 'true';
  });
  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem('darkMode');
    return saved === 'true';
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [showCollected, setShowCollected] = useState(() => {
    const saved = localStorage.getItem('showCollected');
    return saved === 'true';
  });
  const [collectedConferences, setCollectedConferences] = useState<string[]>(() => {
    const saved = localStorage.getItem('collectedConferences');
    return saved ? JSON.parse(saved) : [];
  });

  useEffect(() => {
    const loadData = async () => {
      // Load configuration
      let years = [2026]; // Default fallback
      let title = 'Deadlines'; // Default fallback
      
      try {
        const configResponse = await fetch('config.json');
        const config = await configResponse.json();
        if (config.deadlineYears) {
          years = config.deadlineYears;
        }
        if (config.siteTitle) {
          title = config.siteTitle;
          setSiteTitle(title);
        }
      } catch (error) {
        console.error('Error loading config.json:', error);
      }

      // Load conference data and categories from conf_*.json files
      const allConferences: Conference[] = [];
      const allCategories = new Set<string>();
      
      for (const year of years) {
        try {
          const response = await fetch(`conf_${year}.json`);
          const data = await response.json();
          
          // Handle both old array format and new object format
          if (Array.isArray(data)) {
            // Old format: array of conferences
            allConferences.push(...data);
            // Extract categories from conferences for fallback
            data.forEach(conf => allCategories.add(conf.category));
          } else {
            // New format: object with categories and conferences
            if (data.conferences) {
              allConferences.push(...data.conferences);
            }
            if (data.categories) {
              data.categories.forEach((cat: string) => allCategories.add(cat));
            }
          }
        } catch (error) {
          console.error(`Error loading conf_${year}.json:`, error);
        }
      }
      
      setConferences(allConferences);
      setCategories(Array.from(allCategories));
    };
    loadData();
  }, []);

  useEffect(() => {
    localStorage.setItem('darkMode', darkMode.toString());
  }, [darkMode]);

  useEffect(() => {
    localStorage.setItem('categoryFilter', categoryFilter);
  }, [categoryFilter]);

  useEffect(() => {
    localStorage.setItem('showPassed', showPassed.toString());
  }, [showPassed]);

  useEffect(() => {
    localStorage.setItem('showCollected', showCollected.toString());
  }, [showCollected]);

  useEffect(() => {
    localStorage.setItem('collectedConferences', JSON.stringify(collectedConferences));
  }, [collectedConferences]);



  const now = new Date();
  const filteredConferences = conferences.filter(conf => {
    const deadline = new Date(conf.deadline + 'T23:59:59');
    const matchesSearch = conf.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         conf.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         conf.location.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = !categoryFilter || conf.category === categoryFilter;
    const matchesDeadline = showPassed || deadline > now;
    const matchesCollected = !showCollected || collectedConferences.includes(conf.name);
    
    return matchesSearch && matchesCategory && matchesDeadline && matchesCollected;
  });

  const sortedConferences = [...filteredConferences].sort((a, b) => {
    let cmp = 0;
    if (sortKey === 'deadline') {
      cmp = new Date(a.deadline + 'T23:59:59').getTime() - new Date(b.deadline + 'T23:59:59').getTime();
    } else if (sortKey === 'category') {
      cmp = a.category.localeCompare(b.category);
    } else if (sortKey === 'rating') {
      cmp = a.rating.localeCompare(b.rating);
    } else if (sortKey === 'daysLeft') {
      const daysLeftA = Math.ceil((new Date(a.deadline + 'T23:59:59').getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
      const daysLeftB = Math.ceil((new Date(b.deadline + 'T23:59:59').getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
      cmp = daysLeftA - daysLeftB;
    }
    return sortOrder === 'asc' ? cmp : -cmp;
  });

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortOrder('asc');
    }
  };

  const toggleDarkMode = () => {
    setDarkMode(!darkMode);
  };

  const toggleCollect = (conferenceName: string) => {
    setCollectedConferences(prev => {
      if (prev.includes(conferenceName)) {
        return prev.filter(name => name !== conferenceName);
      } else {
        return [...prev, conferenceName];
      }
    });
  };

  const getRatingStars = (rating: string) => {
    switch (rating) {
      case 'A': return 3;
      case 'B': return 2;
      case 'C': return 1;
      case 'N/A': return 0;
      default: return 0;
    }
  };

  const formatRating = (rating: string) => {
    if (rating === 'N/A') {
      return 'N/A';
    }
    return '';
  };

  return (
    <Router>
      <div className={`Main ${darkMode ? 'dark-mode' : ''}`}>
        <Navigation />
        <Routes>
          <Route path="/" element={<ConferenceLinks darkMode={darkMode} onToggleDarkMode={toggleDarkMode} />} />
          <Route path="/links" element={<Navigate to="/" replace />} />
          <Route path="/deadlines" element={
            <>
              <h1>{siteTitle}</h1>
              <div className="controls">
                <div className="search-group">
                  <input
                    type="text"
                    placeholder="Search by name, category, or location"
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                    className="search-input"
                  />
                </div>
                <div className="controls-group">
                  <label>
                    Filter by category:
                    <select value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)}>
                      <option value="">All</option>
                      {categories.map(cat => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                    </select>
                  </label>
                  <label>
                    <input
                      type="checkbox"
                      checked={showPassed}
                      onChange={e => setShowPassed(e.target.checked)}
                    />
                    Show passed deadlines
                  </label>
                  <label>
                    <input
                      type="checkbox"
                      checked={showCollected}
                      onChange={(e) => setShowCollected(e.target.checked)}
                    />
                    Show Collected Only
                  </label>
                  <button onClick={toggleDarkMode} className="theme-toggle">
                    {darkMode ? '☀️ Light Mode' : '🌙 Dark Mode'}
                  </button>
                </div>
              </div>
              <table className="conf-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th onClick={() => handleSort('deadline')} className="sortable">
                      Deadline {sortKey === 'deadline' ? (sortOrder === 'asc' ? '▲' : '▼') : ''}
                    </th>
                    <th onClick={() => handleSort('daysLeft')} className="sortable">
                      Days Left {sortKey === 'daysLeft' ? (sortOrder === 'asc' ? '▲' : '▼') : ''}
                    </th>
                    <th onClick={() => handleSort('category')} className="sortable">
                      Category {sortKey === 'category' ? (sortOrder === 'asc' ? '▲' : '▼') : ''}
                    </th>
                    <th>Location</th>
                    <th onClick={() => handleSort('rating')} className="sortable">
                      Rating {sortKey === 'rating' ? (sortOrder === 'asc' ? '▲' : '▼') : ''}
                    </th>
                    <th>Link</th>
                    <th>Collect</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedConferences.map((conf) => {
                    const deadline = new Date(conf.deadline + 'T23:59:59');
                    const now = new Date();
                    const daysLeft = Math.ceil((deadline.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
                    return (
                      <tr key={conf.name}>
                        <td data-label="Name">{conf.name}</td>
                        <td data-label="Deadline">{conf.deadline}</td>
                        <td data-label="Days Left">{daysLeft > 0 ? `${daysLeft} days` : 'Passed'}</td>
                        <td data-label="Category">{conf.category}</td>
                        <td data-label="Location">{conf.location}</td>
                        <td data-label="Rating">
                          {formatRating(conf.rating)} {'★'.repeat(getRatingStars(conf.rating))}
                        </td>
                        <td data-label="Link"><a href={conf.link} target="_blank" rel="noopener noreferrer">🌐</a></td>
                        <td data-label="Collect">
                          <button
                            onClick={() => toggleCollect(conf.name)}
                            className={`collect-btn ${collectedConferences.includes(conf.name) ? 'collected' : ''}`}
                          >
                            {collectedConferences.includes(conf.name) ? '★' : '☆'}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <footer style={{ 
                textAlign: 'center', 
                padding: '25px 20px',
                marginTop: '50px',
                color: '#666',
                fontSize: '0.9rem',
                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                background: 'transparent'
              }}>
                <div style={{ 
                  maxWidth: '500px', 
                  margin: '0 auto',
                  lineHeight: '1.5'
                }}>
                  ©{' '}
                  <a 
                    href="https://linmao.org" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    style={{
                      color: '#0066cc',
                      textDecoration: 'none',
                      fontWeight: '500',
                      transition: 'color 0.2s ease'
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.color = '#004999';
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.color = '#0066cc';
                    }}
                  >
                    Mao Lin
                  </a>
                  {' '}{new Date().getFullYear()}
                </div>
              </footer>
            </>
          } />
        </Routes>
      </div>
    </Router>
  );
}

export default Main;
